import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Mail, 
  Edit2, 
  Send, 
  RotateCcw, 
  CheckCircle2,
  FileText,
  User,
  Clock,
  Search,
  X
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Send email state
  const [showSendForm, setShowSendForm] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [sending, setSending] = useState(false);
  
  // Recent logs
  const [recentLogs, setRecentLogs] = useState([]);
  
  const adminKey = 'luma-admin-2024'; // For demo - in production use JWT

  useEffect(() => {
    fetchTemplates();
    fetchRecentLogs();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/?admin_key=${adminKey}`);
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({ title: 'Error', description: 'Failed to load templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/logs/recent?admin_key=${adminKey}&limit=10`);
      const data = await response.json();
      setRecentLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditedSubject(template.subject);
    setEditedBody(template.body);
    setEditMode(false);
    setShowSendForm(false);
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/email-templates/${selectedTemplate.name}?admin_key=${adminKey}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subject: editedSubject,
            body: editedBody
          })
        }
      );
      
      if (response.ok) {
        toast({ title: 'Saved', description: 'Template updated successfully' });
        setEditMode(false);
        fetchTemplates();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!selectedTemplate) return;
    
    if (!window.confirm('Reset this template to default? Your customizations will be lost.')) {
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/email-templates/${selectedTemplate.name}/reset?admin_key=${adminKey}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        toast({ title: 'Reset', description: 'Template reset to default' });
        fetchTemplates();
        setSelectedTemplate(null);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reset template', variant: 'destructive' });
    }
  };

  const sendEmail = async () => {
    if (!recipientEmail || !recipientName || !selectedTemplate) return;
    
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/email-templates/send?admin_key=${adminKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            template_name: selectedTemplate.name,
            variables: { candidate_name: recipientName }
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast({ 
          title: 'Email Logged', 
          description: `Email to ${recipientEmail} has been logged. (Sending paused for MVP)`
        });
        setShowSendForm(false);
        setRecipientEmail('');
        setRecipientName('');
        fetchRecentLogs();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to send', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send email', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <Mail className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Email Templates</h2>
            <p className="text-sm text-gray-500">Manage and send email templates</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Template List */}
        <Card className="lg:col-span-1">
          <CardHeader className="py-4 border-b">
            <CardTitle className="text-base flex items-center">
              <FileText className="h-4 w-4 mr-2" />
              Templates ({templates.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {templates.map((template) => (
                  <button
                    key={template.name}
                    onClick={() => selectTemplate(template)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedTemplate?.name === template.name ? 'bg-purple-50 border-l-2 border-purple-500' : ''
                    }`}
                    data-testid={`template-${template.name}`}
                  >
                    <div className="flex items-start justify-between">
                      <p className="font-medium text-gray-900 text-sm">{template.title}</p>
                      {template.is_customised && (
                        <Badge variant="outline" className="text-xs">Edited</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 capitalize">{template.category}</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Template Editor */}
        <Card className="lg:col-span-2">
          {selectedTemplate ? (
            <>
              <CardHeader className="py-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{selectedTemplate.title}</CardTitle>
                  <div className="flex space-x-2">
                    {!editMode ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-template-btn">
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowSendForm(!showSendForm)} data-testid="send-template-btn">
                          <Send className="h-4 w-4 mr-1" />
                          Send
                        </Button>
                        {selectedTemplate.is_customised && (
                          <Button variant="ghost" size="sm" onClick={resetTemplate}>
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Reset
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={saveTemplate} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-4 space-y-4">
                {/* Send Form */}
                {showSendForm && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3 border">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">Send Email</p>
                      <Button variant="ghost" size="sm" onClick={() => setShowSendForm(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Recipient Name</label>
                        <Input
                          placeholder="John Smith"
                          value={recipientName}
                          onChange={(e) => setRecipientName(e.target.value)}
                          data-testid="recipient-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Recipient Email</label>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          data-testid="recipient-email-input"
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={sendEmail} 
                      disabled={sending || !recipientEmail || !recipientName}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="confirm-send-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sending ? 'Sending...' : 'Send Email'}
                    </Button>
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      Note: Email sending is currently in demo mode. Emails are logged but not actually sent.
                    </p>
                  </div>
                )}
                
                {/* Subject */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Subject</label>
                  {editMode ? (
                    <Input
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      className="font-mono text-sm"
                      data-testid="subject-input"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg text-sm">{selectedTemplate.subject}</div>
                  )}
                </div>
                
                {/* Body */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Body</label>
                  {editMode ? (
                    <textarea
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      className="w-full h-64 p-3 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="body-textarea"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap font-mono h-64 overflow-y-auto">
                      {selectedTemplate.body}
                    </div>
                  )}
                </div>
                
                {/* Variables */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Available Variables</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.variables?.map((v) => (
                      <Badge key={v} variant="secondary" className="font-mono text-xs">
                        {`{{${v}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </>
          ) : (
            <div className="h-[500px] flex items-center justify-center">
              <div className="text-center">
                <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a template to view or edit</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Recent Email Logs */}
      <Card>
        <CardHeader className="py-4 border-b">
          <CardTitle className="text-base flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Recent Email Logs
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {recentLogs.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No emails sent yet</div>
          ) : (
            <div className="divide-y">
              {recentLogs.map((log) => (
                <div key={log.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{log.recipient_name}</p>
                      <p className="text-xs text-gray-500">{log.recipient_email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-700">{log.subject}</p>
                    <p className="text-xs text-gray-400">{formatDate(log.sent_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailTemplates;
